import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Polygon } from 'geojson';

import { black, deepGrey, grey, white } from '@methanesat/colors';
import { TextLayer, IconLayer, Layer, PolygonLayer, Color } from '@methanesat/maps';
import { getMonthDay, useTheme } from '@methanesat/ui-components';

import { DEFAULT_TARGET_FILL_COLOR, ZOOM_THRESHOLDS, getFilledTargetColor, opacityTransition } from '../../../consts';
import {
    selectActiveCapture,
    selectAreaEmissionsWithinZoom,
    selectMethaneProduct,
    selectMethaneTargetLayerConfig,
    selectPlatform,
    selectTargetDates
} from '../../../reducers';
import {
    AreaEmissionsProducts,
    CaptureFeature,
    CaptureFeatureProperties,
    PageIds,
    StacFeature,
    StacSearchResponse
} from '../../../types';
import {
    extractPolygonCoords,
    filterL4CapturesWithL3,
    filterPointCapturesIfParentPresent,
    getStacCaptureEmissions,
    getCentroidOfPolygon
} from '../../../utils';

import {
    getCapturesToDisplay,
    useGetAllTargetsWithNumberOfCaptures,
    useGetCapturesWithinViewport,
    useGetSingleCaptureWithinViewport
} from '../../data';

const TARGET_ICON_MAPPING = {
    multiCaptureIcon: { x: 0, y: 0, width: 62, height: 63 },
    /** using the pill icon with `mask: true` allows us to chose its color dynamically */
    pillIcon: { x: 0, y: 0, width: 50, height: 22, mask: true }
};

/**
 * Builds the deck.gl layer for the MSat target layer
 */
export const useTargetLayers = (
    zoom: number,
    data: StacSearchResponse<Polygon, CaptureFeatureProperties> | null,
    page?: string
): Layer[] => {
    const theme = useTheme();
    const prefersDarkMode: boolean = theme.palette.mode === 'dark';
    const { highlightedFeatureId, ...layerProps } = useSelector(selectMethaneTargetLayerConfig);

    const [hasUpdatedLayers, setHasUpdatedLayers] = useState(false);
    const [L4Captures, setL4Captures] = useState<CaptureFeature[]>([]);
    const [L3Captures, setL3Captures] = useState<StacFeature<Polygon, CaptureFeatureProperties>[]>([]);

    const activeCapture = useSelector(selectActiveCapture);
    const visibleCaptures = useGetCapturesWithinViewport(data);
    const singleCaptureInView = useGetSingleCaptureWithinViewport(visibleCaptures);

    const product = useSelector(selectMethaneProduct);
    const areaEmissionsWithinZoom = useSelector(selectAreaEmissionsWithinZoom);

    // determine time range
    // create new time item "string_from/string_til"
    // modify the search and send
    const platform = useSelector(selectPlatform);
    const targetDates = useSelector(selectTargetDates);

    // Get captures to display when the data is ready or when it changes
    useEffect(() => {
        if (data && data.features) {
            setHasUpdatedLayers(false);

            // Get L3 and L4 captures to display
            const { [AreaEmissionsProducts.l3]: L3Captures, [AreaEmissionsProducts.l4]: L4Captures } =
                getCapturesToDisplay(data.features, targetDates, product);
            const L4CapturesWithPointsFiltered = filterPointCapturesIfParentPresent(L4Captures);
            const L4CapturesWithL3 = filterL4CapturesWithL3(L4CapturesWithPointsFiltered, L3Captures);

            // Set state
            setL3Captures(L3Captures);
            setL4Captures(product === AreaEmissionsProducts.l4 ? L4CapturesWithPointsFiltered : L4CapturesWithL3);

            setHasUpdatedLayers(true);
        }
    }, [data, product, singleCaptureInView, targetDates]);

    /** Map<targetId|targetGroupId, countOfCaptures> */
    const targetsWithNumberOfCaptures = useGetAllTargetsWithNumberOfCaptures();

    // list used to build icon layer indicating a target has multiple captures
    const multiCapture: CaptureFeature[] = useMemo(() => {
        const result: CaptureFeature[] = [];
        L4Captures.forEach((capture) => {
            const targetOrGroupId = capture.properties.target_group_id || capture.properties.target_id;
            if (targetsWithNumberOfCaptures.get(targetOrGroupId) > 1) {
                result.push(capture);
            }
        });
        return result;
    }, [L4Captures, targetsWithNumberOfCaptures]);

    const getDateChipPixelOffset = useCallback(
        (d: CaptureFeature): [number, number] => {
            const targetOrGroupId = d.properties.target_group_id || d.properties.target_id;
            // the single collection page doesn't show the multicapture icon, so the offset isn't needed on that page
            return targetsWithNumberOfCaptures.get(targetOrGroupId) > 1 && page !== PageIds.singleCollection
                ? [0, 18]
                : [0, 0];
        },
        [targetsWithNumberOfCaptures, page]
    );

    // never show multi-capture icon on the single collection page (see `visible` prop)
    const multipleCaptureIcon = new IconLayer({
        data: hasUpdatedLayers ? multiCapture : undefined,
        getIcon: () => 'multiCaptureIcon',
        getPosition: getCentroidOfPolygon,
        getSize: 36,
        iconAtlas: '/img/multi-target-icon.svg',
        iconMapping: TARGET_ICON_MAPPING,
        id: layerProps.id + 'multiple-capture-icon',
        minZoom: 6,
        pickable: true,
        visible:
            zoom > ZOOM_THRESHOLDS.MINIMUM_ZOOM_LEVEL_TARGET_MULTI_CAPTURE_ICON &&
            zoom < ZOOM_THRESHOLDS.MAXIMUM_ZOOM_LEVEL_TARGET_MULTI_CAPTURE_ICON &&
            page !== PageIds.singleCollection,
        updateTriggers: {
            getPixelOffset: targetsWithNumberOfCaptures.size
        }
    });

    const pillIconVisibility =
        zoom > ZOOM_THRESHOLDS.MINIMUM_ZOOM_LEVEL_TARGET_DATE_LABEL &&
        zoom < ZOOM_THRESHOLDS.MAXIMUM_ZOOM_LEVEL_TARGET_DATE_LABEL;

    const pillIcon = new IconLayer({
        data: L4Captures,
        getIcon: () => 'pillIcon',
        getPosition: getCentroidOfPolygon,
        getColor: (d) => {
            const shouldHighlight =
                d.id === (highlightedFeatureId || singleCaptureInView?.id || activeCapture?.itemId) || !activeCapture;
            if (prefersDarkMode) {
                return shouldHighlight ? white : grey;
            }
            return shouldHighlight ? black : deepGrey;
        },
        getSize: 25,
        getPixelOffset: getDateChipPixelOffset,
        iconAtlas: '/img/black-pill-icon.svg',
        iconMapping: TARGET_ICON_MAPPING,
        id: layerProps.id + 'black-pill-icon',
        minZoom: 6,
        pickable: zoom <= ZOOM_THRESHOLDS.MAXIMUM_ZOOM_LEVEL_TARGET,
        visible: pillIconVisibility,
        opacity: pillIconVisibility ? 1 : 0,
        transitions: {
            opacity: { opacityTransition }
        },
        updateTriggers: {
            getPixelOffset: targetsWithNumberOfCaptures.size,
            pickable: zoom,
            getColor: [activeCapture, singleCaptureInView?.id, highlightedFeatureId, prefersDarkMode]
        }
    });

    const dateLabelVisibility =
        zoom > ZOOM_THRESHOLDS.MINIMUM_ZOOM_LEVEL_TARGET_DATE_LABEL &&
        zoom < ZOOM_THRESHOLDS.MAXIMUM_ZOOM_LEVEL_TARGET_DATE_LABEL;

    const dateLabel = new TextLayer({
        data: L4Captures,
        getPosition: (d) => {
            return getCentroidOfPolygon(d);
        },
        getSize: 12,
        getText: (d) => getMonthDay(d.properties.datetime).toUpperCase(),
        getPixelOffset: getDateChipPixelOffset,
        fontFamily: 'Roboto',
        getColor: prefersDarkMode ? [0, 0, 0] : [255, 255, 255],
        getTextAnchor: 'middle',
        id: layerProps.id + 'date-icon',
        minZoom: 6,
        pickable: true,
        visible: dateLabelVisibility,
        opacity: dateLabelVisibility ? 1 : 0,
        transitions: {
            opacity: opacityTransition
        },
        updateTriggers: {
            getPixelOffset: targetsWithNumberOfCaptures.size,
            getColor: prefersDarkMode
        }
    });

    const getLineWidth = (d: CaptureFeature) => {
        const isTargetLevel = zoom < ZOOM_THRESHOLDS.MAXIMUM_ZOOM_LEVEL_TARGET;
        const shouldHighlight = d.id === (highlightedFeatureId || singleCaptureInView?.id || activeCapture?.itemId);
        return shouldHighlight && isTargetLevel ? 2 : 1;
    };

    const getLineColor = (d: CaptureFeature): Color => {
        const shouldHighlight =
            d.id === (highlightedFeatureId || singleCaptureInView?.id || activeCapture?.itemId) || !activeCapture;

        if (prefersDarkMode) {
            return shouldHighlight ? white : grey;
        }
        return shouldHighlight ? black : deepGrey;
    };

    const targetFilledPolygons = new PolygonLayer<CaptureFeature>({
        ...layerProps,
        id: `${layerProps.id}-filled-polygon`,
        data: hasUpdatedLayers ? L4Captures : [],
        filled: true,
        getFillColor: (d) => {
            const totalMethane = getStacCaptureEmissions(d.properties).totalKgHr;
            const hasTotal = !!totalMethane;
            const hasFill = zoom < ZOOM_THRESHOLDS.MAXIMUM_ZOOM_LEVEL_TARGET;

            return hasTotal && hasFill ? getFilledTargetColor(totalMethane, platform) : DEFAULT_TARGET_FILL_COLOR;
        },
        getPolygon: (d) => extractPolygonCoords(d),
        stroked: false,
        updateTriggers: { getLineWidth: highlightedFeatureId, getFillColor: zoom },
        visible: zoom < ZOOM_THRESHOLDS.MAXIMUM_ZOOM_LEVEL_TARGET,
        opacity: zoom < ZOOM_THRESHOLDS.MAXIMUM_ZOOM_LEVEL_TARGET ? 1 : 0,
        transitions: {
            opacity: {
                duration: 300,
                easing: (x: number) => (x === 0 ? 0 : Math.pow(2, 10 * x - 10))
            }
        }
    });

    const targetOutlines = new PolygonLayer<CaptureFeature>({
        ...layerProps,
        id: `${layerProps.id}-outlines`,
        data: hasUpdatedLayers ? L4Captures : [],

        filled: false,
        getLineColor,
        getLineWidth: getLineWidth,
        getPolygon: (d) => extractPolygonCoords(d),
        lineWidthMaxPixels: 5,
        lineWidthMinPixels: 1,
        lineWidthUnits: 'pixels',
        pickable: zoom < ZOOM_THRESHOLDS.MAXIMUM_ZOOM_LEVEL_TARGET,
        stroked: true,
        updateTriggers: {
            getLineColor: [highlightedFeatureId, singleCaptureInView?.id, activeCapture, prefersDarkMode],
            getLineWidth: [highlightedFeatureId, singleCaptureInView?.id, activeCapture, zoom]
        },
        visible: true,
        transitions: {
            getLineWidth: {
                duration: 300
            }
        }
    });

    return [targetFilledPolygons, targetOutlines, multipleCaptureIcon, pillIcon, dateLabel];
};
